import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { FiletypeCsv } from 'react-bootstrap-icons';
import { Calendar, Clock, CreditCard, Layers, Search as SearchIcon } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'sonner';
import ChipSkeletonLoader from '../common/ChipSkeletonLoader';
import DataTable from '../common/data-table/DataTable';
import CustomMultiSelect from '../CustomMultiSelect';
import CancelRequestModal from '../services/pipeline/overview/modals/CancellationRequestModal';
import ClosingChangeRequestModal from '../services/pipeline/overview/modals/ClosingChangeRequestModal';
import UndoCancellationModal from '../services/pipeline/overview/modals/UndoCancellationModal';
import { ABORT_ERROR, makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS, INVOICE_STATUS_CONFIG } from '../../helpers/constants';
import BillingNoteModal from './BillingNoteModal';
import BillingNotesSheet from './BillingNotesSheet';
import ClientViewConfirmModal from './ClientViewConfirmModal';
import MarkInvoicePaidModal from './MarkInvoicePaidModal';
import WireInformationModal from './WireInformationModal';
import { getStatementRowStyle, getStatementTableColumns } from './statementTableColumns';
import ServiceSlidingBar from '../orders/ServiceSlidingBar';
import useCancellationRequest from '../../hooks/useCancellationRequest';
import useClosingChangeRequest from '../../hooks/useClosingChangeRequest';
import { formatCurrency, getUserFromLocalstrorage, individualOrdersOption, isAdmin, isClient } from '../../helpers/global';

const EMPTY_SUMMARY = {
  dueCount: 0,
  dueAmount: 0,
  overdueCount: 0,
  overdueAmount: 0,
  checkSentCount: 0,
  cancellationRequestedCount: 0,
  unpaidCount: 0,
  outstandingBalance: 0
};

const DEFAULT_SORT = { key: 'closingDate', direction: 'asc' };

const STATEMENT_FETCH_MODES = {
  REPLACE: 'replace',
  APPEND: 'append',
  RECONCILE: 'reconcile'
};

const STATUS_FILTER_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Due', value: 'Due' },
  { label: 'Overdue', value: 'Overdue' },
  { label: 'Check Sent', value: 'Check Sent' },
  { label: 'Cancellation Requested', value: 'Cancellation Requested' }
];

const CLOSING_FILTER_TABS = [
  { key: 'all', label: 'All Open', Icon: Layers, bgColor: '#000000', textColor: 'white' },
  { key: 'awaiting', label: 'Awaiting Closing', Icon: Clock, bgColor: '#FACC15', textColor: 'black' },
  { key: 'past', label: 'Past Closing', Icon: Calendar, bgColor: '#F97316', textColor: 'white' }
];

const getStatementOrderId = statement => statement?.orderId?._id || statement?.orderId;

const UnpaidStatementsInvoicesTab = ({
  onTabCountsChange,
  viewingAsClient = null,
  onViewAsClientTargetChange
}) => {
  const isClientView = Boolean(viewingAsClient);
  const [statements, setStatements] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [showWireInformationModal, setShowWireInformationModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(STATUS_FILTER_OPTIONS[0]);
  const [closingFilter, setClosingFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState({});
  const [billingNotesSheetRow, setBillingNotesSheetRow] = useState(null);
  const [billingNoteModalOpen, setBillingNoteModalOpen] = useState(false);
  const [billingNoteOrderId, setBillingNoteOrderId] = useState(null);
  const [billingNoteText, setBillingNoteText] = useState('');
  const [billingNoteSubmitting, setBillingNoteSubmitting] = useState(false);
  const [markPaidConfirmRow, setMarkPaidConfirmRow] = useState(null);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [undoCancellationRow, setUndoCancellationRow] = useState(null);
  const [showUndoCancellationModal, setShowUndoCancellationModal] = useState(false);
  const [serviceMetadata, setServiceMetadata] = useState(null);
  const [clientViewConfirm, setClientViewConfirm] = useState(null);
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const currentUserInfo = useMemo(() => getUserFromLocalstrorage(), []);
  const isAdminRole = isAdmin(currentUserInfo?.role);
  const isAdminUser = isAdminRole && !isClientView;
  const isClientUser = isClient(currentUserInfo?.role) || isClientView;
  const viewAsOrganizationId = viewingAsClient?.organizationId;

  const cancellation = useCancellationRequest({
    viewAsOrganizationId,
    onSuccess: ({ statementId, orderId, cancellationRequested = true } = {}) => {
      setStatements(prev =>
        prev.map(statement => {
          const isTargetStatement = statementId && statement._id === statementId;
          const isTargetOrder = orderId && getStatementOrderId(statement) === orderId;

          if (!isTargetStatement && !isTargetOrder) return statement;

          return {
            ...statement,
            isFileCancelled: cancellationRequested,
            status: cancellationRequested
              ? 'Cancellation Requested'
              : statement.isCheckSent || statement.status === 'Check Sent'
              ? 'Check Sent'
              : 'Due',
            orderId:
              statement.orderId && typeof statement.orderId === 'object'
                ? { ...statement.orderId, cancellationRequested }
                : statement.orderId
          };
        })
      );
      fetchStatements({ page: 1, mode: STATEMENT_FETCH_MODES.RECONCILE });
    }
  });

  const closingChange = useClosingChangeRequest({
    viewAsOrganizationId,
    onSuccess: (response, meta = {}) => {
      const serviceId = response?._id || meta.serviceId;
      const closingDate = response?.closingDate || meta.values?.newClosingDate;

      if (serviceId && closingDate) {
        setStatements(prev =>
          prev.map(statement => {
            const statementServiceId = statement.serviceId?._id || statement.serviceId;
            if (statementServiceId !== serviceId) return statement;

            return {
              ...statement,
              closingDate,
              serviceId:
                statement.serviceId && typeof statement.serviceId === 'object'
                  ? { ...statement.serviceId, closingDate, newClosingDate: null }
                  : { _id: serviceId, closingDate }
            };
          })
        );
      }

      fetchStatements({ page: 1, mode: STATEMENT_FETCH_MODES.RECONCILE });
    }
  });

  useEffect(() => {
    if (!isAdminRole || !currentUserInfo?.organization?.length) return;

    const orgOptions = currentUserInfo.organization
      .map(org => ({
        value: org._id,
        label: org.name,
        accurateID: org.accurateID || null,
        isEnabled: org.isEnabled
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const nextOptions = [individualOrdersOption, ...orgOptions];

    setOrganizations(prev => {
      if (
        prev.length === nextOptions.length &&
        prev.every((option, index) => option.value === nextOptions[index].value)
      ) {
        return prev;
      }

      return nextOptions;
    });
  }, [currentUserInfo, isAdminRole]);

  const fetchStatements = useCallback(
    async ({
      page = 1,
      pageSize = undefined,
      mode = STATEMENT_FETCH_MODES.REPLACE,
      sort = sortConfig,
      search = searchQuery,
      organizationIds = selectedOrganizations,
      status = selectedStatus?.value ?? '',
      closing = closingFilter
    } = {}) => {
      const isAppend = mode === STATEMENT_FETCH_MODES.APPEND;
      const isReconcile = mode === STATEMENT_FETCH_MODES.RECONCILE;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (isReconcile) {
        setSummaryLoading(true);
      } else if (isAppend) {
        setSummaryLoading(false);
        setLoadingMore(true);
      } else {
        setSummaryLoading(false);
        setLoading(true);
      }

      const mongoSort = { [sort.key]: sort.direction === 'asc' ? 1 : -1 };

      const requestBody = {
        page,
        pageSize,
        sort: mongoSort,
        search
      };

      if (isClientView && viewAsOrganizationId) {
        // Same org filter admin already uses — scoped to the viewed client
        requestBody.organizationIds = [viewAsOrganizationId];
        requestBody.viewAsOrganizationId = viewAsOrganizationId;
        requestBody.closingFilter = closing || 'all';
      } else {
        if (isAdminUser && organizationIds.length > 0) {
          requestBody.organizationIds = organizationIds;
        }

        if (isAdminUser && status) {
          requestBody.status = status;
        }

        if (!isAdminUser) {
          requestBody.closingFilter = closing || 'all';
        }
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INVOICE_STATEMENTS,
        method: 'POST',
        requestBody,
        signal: controller.signal
      });

      if (error === ABORT_ERROR) {
        if (isAppend) {
          setLoadingMore(false);
        } else if (!isReconcile) {
          setLoading(false);
        }
        return;
      }

      if (error) {
        if (isAppend) {
          setLoadingMore(false);
        } else if (isReconcile) {
          setSummaryLoading(false);
        } else {
          setLoading(false);
        }
        toast.error(error);
        return;
      }

      const newStatements = response?.statements || [];

      onTabCountsChange?.({
        unpaidCount: response?.unpaidCount,
        pendingReconciliationCount: response?.pendingReconciliationCount
      });

      if (isAppend) {
        setStatements(prev => [...prev, ...newStatements]);
      } else if (isReconcile) {
        setSummary(response?.summary || EMPTY_SUMMARY);
      } else {
        setStatements(newStatements);
        setSummary(response?.summary || EMPTY_SUMMARY);
      }

      const total = response?.totalCount ?? 0;
      const totalPages = response?.totalPages ?? 1;
      setTotalCount(total);
      if (!isReconcile || isAppend) {
        setHasMoreData(page < totalPages);
        setCurrentPage(page);
      }

      if (isAppend) {
        setLoadingMore(false);
      } else if (isReconcile) {
        setSummaryLoading(false);
      } else {
        setLoading(false);
      }
    },
    [
      sortConfig,
      searchQuery,
      selectedOrganizations,
      selectedStatus?.value,
      closingFilter,
      isAdminUser,
      isClientView,
      viewAsOrganizationId,
      onTabCountsChange
    ]
  );

  useEffect(() => {
    setCurrentPage(1);
    setHasMoreData(true);
    fetchStatements({ page: 1, mode: STATEMENT_FETCH_MODES.REPLACE });
  }, [fetchStatements, sortConfig, searchQuery, selectedOrganizations, selectedStatus?.value, closingFilter]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = useCallback(e => {
    const query = e.target.value;
    setSearchInput(query);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setSearchQuery(query.trim());
      setCurrentPage(1);
      setHasMoreData(true);
    }, 500);
  }, []);

  const handleOrganizationChange = useCallback(selectedValues => {
    setSelectedOrganizations(selectedValues.map(item => item.value));
    setCurrentPage(1);
    setHasMoreData(true);
  }, []);

  const handleStatusChange = useCallback(option => {
    setSelectedStatus(option);
    setCurrentPage(1);
    setHasMoreData(true);
  }, []);

  const handleClosingFilterChange = useCallback(filterKey => {
    setClosingFilter(filterKey);
    setCurrentPage(1);
    setHasMoreData(true);
  }, []);

  const handleSortChange = useCallback(newSort => {
    setSortConfig(newSort);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (loading || loadingMore || !hasMoreData) return;
    fetchStatements({ page: currentPage + 1, mode: STATEMENT_FETCH_MODES.APPEND });
  }, [loading, loadingMore, hasMoreData, currentPage, fetchStatements]);

  const handleDownloadCsv = useCallback(async () => {
    if (downloadingCsv) return;

    setDownloadingCsv(true);

    try {
      const requestBody = {
        sort: { [sortConfig.key]: sortConfig.direction === 'asc' ? 1 : -1 },
        search: searchQuery
      };

      if (isAdminUser && selectedOrganizations.length > 0) {
        requestBody.organizationIds = selectedOrganizations;
      }

      if (isAdminUser && selectedStatus?.value) {
        requestBody.status = selectedStatus.value;
      }

      if (!isAdminUser) {
        requestBody.closingFilter = closingFilter || 'all';
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INVOICE_STATEMENTS_EXPORT,
        method: 'POST',
        requestBody
      });

      if (error) {
        toast.error(error);
        return;
      }

      const csvData = response?.data?.csv;
      const filename = response?.data?.filename;

      if (!csvData) {
        toast.error('Failed to download CSV.');
        return;
      }

      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          filename || `accurate-lien-search-statement-${new Date().toISOString().slice(0, 10)}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        toast.error('Download is not supported in this browser.');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to download CSV.');
    } finally {
      setDownloadingCsv(false);
    }
  }, [
    downloadingCsv,
    sortConfig,
    searchQuery,
    selectedOrganizations,
    selectedStatus?.value,
    closingFilter,
    isAdminUser
  ]);

  const tableRows = useMemo(
    () =>
      statements.map(s => ({
        ...s,
        closingDate: s.closingDate ? new Date(s.closingDate) : null,
        earliestDueDate: s.earliestDueDate ? new Date(s.earliestDueDate) : null,
        invoiceNumbers: s.invoiceNumbers || '—'
      })),
    [statements]
  );

  const {
    dueCount,
    dueAmount,
    overdueCount,
    overdueAmount,
    checkSentCount,
    unpaidCount,
    outstandingBalance,
    cancellationRequestedCount
  } = summary;

  const summaryBadges = useMemo(
    () => [
      { key: 'unpaid', label: 'Unpaid', count: unpaidCount, statusKey: 'Unpaid' },
      { key: 'due', label: 'Due', count: dueCount, statusKey: 'Due' },
      { key: 'overdue', label: 'Overdue', count: overdueCount, statusKey: 'Overdue' },
      { key: 'check-sent', label: 'Check Sent', count: checkSentCount, statusKey: 'Check Sent' },
      {
        key: 'cancellation-requested',
        label: 'Cancellation Requested',
        count: cancellationRequestedCount,
        statusKey: 'Cancellation Requested'
      }
    ],
    [unpaidCount, dueCount, overdueCount, checkSentCount, cancellationRequestedCount]
  );

  const setActionKey = (key, val) => setActionLoading(prev => ({ ...prev, [key]: val }));

  const updateStatementById = useCallback((statementId, updates) => {
    setStatements(prev =>
      prev.map(statement => (statement._id === statementId ? { ...statement, ...updates } : statement))
    );
  }, []);

  const updateStatementsByOrderId = useCallback((orderId, updates) => {
    if (!orderId) return;

    setStatements(prev =>
      prev.map(statement => (getStatementOrderId(statement) === orderId ? { ...statement, ...updates } : statement))
    );
  }, []);

  const removeStatementById = useCallback(statementId => {
    setStatements(prev => prev.filter(statement => statement._id !== statementId));
    setTotalCount(prev => Math.max(0, prev - 1));
  }, []);

  const reconcileStatementTotals = useCallback(() => {
    fetchStatements({ page: 1, mode: STATEMENT_FETCH_MODES.RECONCILE });
  }, [fetchStatements]);

  const refetchAfterSidebarUpdate = useCallback(() => {
    console.log({statements})
    const targetCount = statements?.length ?? undefined;
    setCurrentPage(1);
    setHasMoreData(true);
    fetchStatements({
      page: 1,
      pageSize: targetCount,
      mode: STATEMENT_FETCH_MODES.REPLACE
    });
  }, [fetchStatements, statements.length]);

  const performCheckSent = useCallback(
    async row => {
      const key = `check-${row._id}`;

      setActionKey(key, true);

      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INVOICE_STATEMENT_WITH_ID(row._id),
        method: 'PATCH',
        requestBody: {
          isCheckSent: true,
          ...(viewAsOrganizationId ? { viewAsOrganizationId } : {})
        }
      });

      setActionKey(key, false);

      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Marked as Check Sent');
      updateStatementById(row._id, {
        isCheckSent: true,
        checkSentDate: new Date().toISOString(),
        status: 'Check Sent'
      });
      reconcileStatementTotals();
    },
    [reconcileStatementTotals, updateStatementById, viewAsOrganizationId]
  );

  const handleCheckSent = useCallback(
    row => {
      if (isClientView) {
        setClientViewConfirm({
          type: 'checkSent',
          row,
          title: 'Confirm Check Sent'
        });
        return;
      }
      performCheckSent(row);
    },
    [isClientView, performCheckSent]
  );

  const handleMarkPaidClick = useCallback(row => {
    setMarkPaidConfirmRow(row);
    setShowMarkPaidModal(true);
  }, []);

  const closeMarkPaidConfirm = useCallback(() => {
    setShowMarkPaidModal(false);
  }, []);

  const handleMarkPaidModalExited = useCallback(() => {
    setMarkPaidConfirmRow(null);
  }, []);

  const handleMarkPaidConfirm = useCallback(async () => {
    if (!markPaidConfirmRow) return;

    const key = `paid-${markPaidConfirmRow._id}`;

    setActionKey(key, true);

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.INVOICE_STATEMENT_MARK_PAID(markPaidConfirmRow._id),
      method: 'POST'
    });

    setActionKey(key, false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Marked as Paid');
    closeMarkPaidConfirm();
    removeStatementById(markPaidConfirmRow._id);
    reconcileStatementTotals();
  }, [markPaidConfirmRow, closeMarkPaidConfirm, reconcileStatementTotals, removeStatementById]);

  const handleFileCancelledClick = useCallback(
    row => {
      const orderId = getStatementOrderId(row);
      cancellation.open(
        {
          _id: orderId,
          orderType: row.orderType,
          cancellationRequested: row.orderId?.cancellationRequested
        },
        { driveFolder: row.serviceId?.driveFolder },
        row._id
      );
    },
    [cancellation]
  );

  const handleUndoCancellationClick = useCallback(row => {
    setUndoCancellationRow(row);
    setShowUndoCancellationModal(true);
  }, []);

  const handleUndoCancellationConfirm = useCallback(async () => {
    if (!undoCancellationRow) return;

    const orderId = getStatementOrderId(undoCancellationRow);
    if (!orderId) return;

    const undoKey = `undo-cancellation-${undoCancellationRow._id}`;
    setActionLoading(prev => ({ ...prev, [undoKey]: true }));
    try {
      await cancellation.undo({ _id: orderId }, undoCancellationRow._id);
      setShowUndoCancellationModal(false);
    } finally {
      setActionLoading(prev => ({ ...prev, [undoKey]: false }));
    }
  }, [cancellation, undoCancellationRow]);

  const handleClosingChangeClick = useCallback(
    row => {
      closingChange.open(row);
    },
    [closingChange]
  );

  const handleBillingNotesClick = useCallback(row => {
    const orderId = getStatementOrderId(row);
    if (!orderId) return;
    setBillingNotesSheetRow(row);
  }, []);

  const closeBillingNotesSheet = useCallback(() => {
    setBillingNotesSheetRow(null);
  }, []);

  const handleBillingNoteAdded = useCallback(
    note => {
      const orderId = note?.order?._id || note?.order || getStatementOrderId(billingNotesSheetRow);

      if (orderId && note) {
        updateStatementsByOrderId(orderId, {
          latestBillingNote: {
            text: note.text,
            author: note.author,
            createdAt: note.createdAt
          }
        });
      }

      reconcileStatementTotals();
    },
    [billingNotesSheetRow, reconcileStatementTotals, updateStatementsByOrderId]
  );

  const closeBillingNoteModal = useCallback(() => {
    setBillingNoteModalOpen(false);
    setBillingNoteOrderId(null);
    setBillingNoteText('');
    setBillingNoteSubmitting(false);
  }, []);

  const handleBillingNoteAddClick = useCallback(row => {
    const orderId = getStatementOrderId(row);
    if (!orderId) return;

    setBillingNoteOrderId(orderId);
    setBillingNoteText('');
    setBillingNoteSubmitting(false);
    setBillingNoteModalOpen(true);
  }, []);

  const handleBillingNoteFieldChange = useCallback((key, value) => {
    if (key === 'text') {
      setBillingNoteText(value);
    }
  }, []);

  const performBillingNoteSubmit = useCallback(async () => {
    const text = billingNoteText.trim();
    if (!billingNoteOrderId || !text || billingNoteSubmitting) return;

    setBillingNoteSubmitting(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.BILLING_NOTES,
      method: 'POST',
      requestBody: {
        orderId: billingNoteOrderId,
        text,
        source: isClientView ? 'via Client View' : 'Statement',
        ...(viewAsOrganizationId ? { viewAsOrganizationId } : {})
      }
    });

    setBillingNoteSubmitting(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Billing note added');
    updateStatementsByOrderId(billingNoteOrderId, {
      latestBillingNote: {
        text: response?.text || text,
        author: response?.author,
        createdAt: response?.createdAt || new Date().toISOString()
      }
    });
    closeBillingNoteModal();
    reconcileStatementTotals();
  }, [
    billingNoteOrderId,
    billingNoteText,
    billingNoteSubmitting,
    closeBillingNoteModal,
    isClientView,
    reconcileStatementTotals,
    updateStatementsByOrderId,
    viewAsOrganizationId
  ]);

  const handleBillingNoteSubmit = useCallback(() => {
    const text = billingNoteText.trim();
    if (!billingNoteOrderId || !text || billingNoteSubmitting) return;

    if (isClientView) {
      setClientViewConfirm({
        type: 'billingNote',
        title: 'Confirm billing note'
      });
      return;
    }

    performBillingNoteSubmit();
  }, [
    billingNoteOrderId,
    billingNoteText,
    billingNoteSubmitting,
    isClientView,
    performBillingNoteSubmit
  ]);

  const handleRowClick = useCallback(row => {
    const orderId = getStatementOrderId(row);
    const orderType = row.orderType;
    const service = row.serviceId;

    if (!orderId || !service?._id || !orderType) return;

    setServiceMetadata({
      service,
      orderType,
      rowOrderId: orderId
    });
  }, []);

  const handlePay = useCallback(async row => {
    if (isClientView) return;

    const key = `pay-${row._id}`;
    setActionKey(key, true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.QUICKBOOKS_STATEMENT_INVOICE(row._id),
      method: 'GET'
    });

    setActionKey(key, false);

    if (error) {
      toast.error(error);
      return;
    }

    const invoiceLink = response?.invoiceLink;
    if (!invoiceLink) {
      toast.error('No payment link is available for this invoice.');
      return;
    }

    window.open(invoiceLink, '_blank', 'noopener,noreferrer');
  }, [isClientView]);

  const viewAsClientTarget = useMemo(() => {
    if (!isAdminUser || selectedOrganizations.length !== 1) return null;

    const organizationId = selectedOrganizations[0];
    const organizationName = organizations.find(org => org.value === organizationId)?.label || 'Client';

    return { organizationId, organizationName };
  }, [isAdminUser, organizations, selectedOrganizations]);

  useEffect(() => {
    onViewAsClientTargetChange?.(viewAsClientTarget);
  }, [onViewAsClientTargetChange, viewAsClientTarget]);

  useEffect(() => {
    return () => onViewAsClientTargetChange?.(null);
  }, [onViewAsClientTargetChange]);

  const handleClientViewConfirm = useCallback(async () => {
    if (!clientViewConfirm) return;

    const pending = clientViewConfirm;
    setClientViewConfirm(null);

    if (pending.type === 'checkSent' && pending.row) {
      await performCheckSent(pending.row);
      return;
    }

    if (pending.type === 'billingNote') {
      await performBillingNoteSubmit();
      return;
    }

    if (pending.type === 'cancellation') {
      await cancellation.submit();
      return;
    }

    if (pending.type === 'closingChange') {
      await closingChange.submit();
    }
  }, [cancellation, clientViewConfirm, closingChange, performBillingNoteSubmit, performCheckSent]);

  const handleCancellationSubmit = useCallback(() => {
    if (isClientView) {
      setClientViewConfirm({
        type: 'cancellation',
        title: 'Confirm cancellation request'
      });
      return;
    }
    cancellation.submit();
  }, [cancellation, isClientView]);

  const handleClosingChangeSubmit = useCallback(() => {
    if (isClientView) {
      setClientViewConfirm({
        type: 'closingChange',
        title: 'Confirm closing date change'
      });
      return;
    }
    closingChange.submit();
  }, [closingChange, isClientView]);

  const columns = useMemo(
    () =>
      getStatementTableColumns({
        onBillingNotesClick: handleBillingNotesClick,
        onBillingNoteAddClick: handleBillingNoteAddClick,
        onClosingChangeClick: handleClosingChangeClick,
        onPay: handlePay,
        onCheckSent: handleCheckSent,
        onMarkPaid: handleMarkPaidClick,
        onFileCancelled: handleFileCancelledClick,
        onUndoCancellation: handleUndoCancellationClick,
        actionLoading,
        currentUserInfo,
        forceClientView: isClientView,
        payDisabled: isClientView
      }),
    [
      actionLoading,
      handleBillingNotesClick,
      handleBillingNoteAddClick,
      handleClosingChangeClick,
      handlePay,
      handleCheckSent,
      handleMarkPaidClick,
      handleFileCancelledClick,
      handleUndoCancellationClick,
      isClientView
    ]
  );

  const emptyMessage = searchQuery ? 'No records match your search.' : 'No unpaid invoices found.';

  const markPaidMultiInvoice = markPaidConfirmRow != null && markPaidConfirmRow.orderHasMultipleInvoices === true;

  const markPaidSubmitting = !markPaidMultiInvoice && !!actionLoading[`paid-${markPaidConfirmRow?._id}`];

  const undoCancellationSubmitting = !!actionLoading[`undo-cancellation-${undoCancellationRow?._id}`];

  const isSummaryLoading = loading || summaryLoading;

  return (
    <>
      <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ marginBottom: 24, gap: 16 }}>
        <div>
          <h6 className="my-3 font-weight-bold xxxlargeFont">Account Statement</h6>
          {isAdminUser ? (
            <div className="d-flex flex-wrap align-items-center" style={{ gap: 8, marginTop: 8 }}>
              {isSummaryLoading ? (
                <ChipSkeletonLoader
                  chipHeight={20}
                  chipWidths={[72, 72, 72, 72]}
                  ariaLabel="Loading statement summary"
                />
              ) : (
                summaryBadges.map(({ key, label, count, statusKey }) =>
                  count > 0 ? (
                    <Badge
                      key={key}
                      className="smallFont py-1 px-2 font-weight-semibold"
                      style={INVOICE_STATUS_CONFIG[statusKey]?.style}
                    >
                      {count} {label}
                    </Badge>
                  ) : null
                )
              )}
            </div>
          ) : (
            <div className="d-flex flex-wrap align-items-center" style={{ gap: 8, marginTop: 8 }}>
              {CLOSING_FILTER_TABS.map(({ key, Icon, label, textColor, bgColor }) => {
                const isActive = closingFilter === key;

                return (
                  <Button
                    key={key}
                    variant="outline-light"
                    type="button"
                    className="d-flex align-items-center gap-2 flex-shrink-0"
                    onClick={() => handleClosingFilterChange(key)}
                    style={{
                      padding: '8px 18px',
                      border: isActive ? '1px solid transparent' : '1px solid #e5e7ea',
                      boxShadow: isActive ? 'inset 0 1px 2px rgba(0,0,0,0.04)' : 'none',
                      borderRadius: '8px',
                      backgroundColor: isActive ? bgColor : 'transparent'
                    }}
                  >
                    <Icon className="icon-md" color={isActive ? textColor : 'black'} />
                    <span
                      className={`ml-2 ${isActive ? 'fw-bold' : 'fw-medium'} ${
                        isActive ? `text-${textColor === 'black' ? 'dark' : 'white'}` : 'text-dark'
                      }`}
                    >
                      {label}
                    </span>
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        <div className="statement-page-summary">
          {isAdminUser ? (
            isSummaryLoading ? (
              <>
                <div className="d-flex flex-column align-items-end">
                  <span className="text-muted xtinyFont">Total Outstanding</span>
                  <ChipSkeletonLoader chipHeight={28} chipWidths={[150]} ariaLabel="Loading total outstanding" className="mt-2" />
                </div>
              </>
            ) : (
              <>
                <div className="d-flex flex-column align-items-end">
                  <span className="text-muted xtinyFont">Total Outstanding</span>
                  <span className="font-weight-bold xxxlargeFont text-danger">{formatCurrency(outstandingBalance)}</span>
                </div>
                <div className="d-flex flex-wrap justify-content-end mt-1 text-right fw-bold" style={{ gap: 8 }}>
                  <span className="text-muted tinyFont">Due: {formatCurrency(dueAmount)}</span>
                  <span className="text-danger tinyFont">Overdue: {formatCurrency(overdueAmount)}</span>
                </div>
              </>
            )
          ) : isSummaryLoading ? (
            <div className="largeFont d-flex flex-column align-items-end" style={{ gap: 10 }}>
              <ChipSkeletonLoader chipHeight={18} chipWidths={[200]} ariaLabel="Loading current amount" />
              <ChipSkeletonLoader chipHeight={18} chipWidths={[200]} ariaLabel="Loading overdue amount" />
              <ChipSkeletonLoader chipHeight={18} chipWidths={[200]} ariaLabel="Loading total open amount" />
            </div>
          ) : (
            <div className="largeFont d-flex flex-column align-items-end" style={{ gap: 4 }}>
              <div>
                <span className="font-weight-bold">Current:</span> {formatCurrency(dueAmount)}
              </div>
              <div>
                <span className="font-weight-bold">Overdue:</span>{' '}
                <span className="text-danger font-weight-bold">{formatCurrency(overdueAmount)}</span>
              </div>
              <div>
                <span className="font-weight-bold">Total Open:</span> {formatCurrency(outstandingBalance)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={`mb-2 statement-page-toolbar statement-page-toolbar--with-actions${
          isAdminUser ? ' statement-page-toolbar--admin' : ''
        }`}
      >
        <div className="statement-page-toolbar__search position-relative" style={{ minWidth: 0, width: '100%' }}>
          <SearchIcon
            className="text-muted flex-shrink-0"
            size={15}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          />
          <Form.Control
            placeholder="Search by invoice no, file, address, service..."
            className="w-100 text-dark xsmallFont rounded-lg bg-white"
            value={searchInput}
            onChange={handleSearchChange}
            style={{ paddingLeft: 30 }}
          />
        </div>

        <div className="statement-page-toolbar__filters">
          {isAdminUser && (
            <>
              <div className="statement-page-toolbar__client d-flex align-items-center flex-shrink-0 p-1">
                <CustomMultiSelect
                  id="statement-organization-selector"
                  onChange={handleOrganizationChange}
                  selectedOptions={selectedOrganizations}
                  options={organizations}
                  placeholder="Select organizations..."
                  maxToShow={2}
                  containerClassName="px-0"
                  className="dashboard_multi_select_custom_style"
                  menuPosition="fixed"
                />
              </div>
              <div className="statement-page-toolbar__status custom-multi-select-container p-1 d-flex align-items-center flex-shrink-0">
                <Select
                  classNamePrefix="select"
                  className="custom-multi-select w-100 dashboard_multi_select_custom_style"
                  placeholder="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: base => ({
                      ...base,
                      zIndex: 99999999
                    })
                  }}
                />
              </div>
            </>
          )}
          {isClientUser && (
            <div className="p-1">
              <Button
                variant="outline-light"
                type="button"
                className="d-flex align-items-center flex-shrink-0 border shadow-none download-button text-dark"
                onClick={() => setShowWireInformationModal(true)}
                style={{
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'var(--muted)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="mr-1 d-inline-flex align-items-center flex-shrink-0">
                  <CreditCard size={16} color="black" />
                </span>
                <span className="xsmallFont">
                  Wire Information
                </span>
              </Button>
            </div>
          )}
          {!isClientView && (
            <div className="p-1">
              <Button
                size="sm"
                variant="success"
                className="d-flex align-items-center download-button flex-shrink-0"
                onClick={handleDownloadCsv}
                disabled={downloadingCsv || loading}
              >
                <FiletypeCsv size={14} className="mr-1" />
                <span className="xsmallFont mb-0">{downloadingCsv ? 'Downloading...' : 'Download CSV'}</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <DataTable
        key={`${sortConfig.key}-${sortConfig.direction}`}
        data={tableRows}
        columns={columns}
        rowKey="_id"
        sortable
        defaultSort={sortConfig}
        onSortChange={handleSortChange}
        pagination={false}
        serverSort
        loading={loading}
        emptyMessage={emptyMessage}
        hover
        maxHeight="calc(100vh - 350px)"
        onRowClick={handleRowClick}
        getRowStyle={getStatementRowStyle}
        onBottomReached={handleLoadMore}
        loadingMoreData={loadingMore}
        bottomOffset={100}
      />

      {totalCount > 0 && !loading && (
        <div className="text-muted mt-2">
          Showing <span className="text-dark font-weight-bold">{totalCount}</span> invoices
        </div>
      )}

      <WireInformationModal
        show={showWireInformationModal}
        onHide={() => setShowWireInformationModal(false)}
      />

      <BillingNoteModal
        show={billingNoteModalOpen}
        onHide={() => !billingNoteSubmitting && closeBillingNoteModal()}
        values={{ text: billingNoteText }}
        onValuesChange={handleBillingNoteFieldChange}
        onSubmit={handleBillingNoteSubmit}
        submitting={billingNoteSubmitting}
      />

      {billingNotesSheetRow && (
        <BillingNotesSheet
          visible
          onClose={closeBillingNotesSheet}
          orderId={billingNotesSheetRow.orderId?._id || billingNotesSheetRow.orderId}
          orderStatus={billingNotesSheetRow.orderId?.status}
          fileNo={billingNotesSheetRow.fileNo}
          invoiceNumber={billingNotesSheetRow.invoiceNumber}
          organizationName={
            billingNotesSheetRow.organizationName ||
            viewingAsClient?.organizationName ||
            currentUserInfo?.organization?.[0]?.name
          }
          propertyAddress={billingNotesSheetRow.propertyAddress}
          onNoteAdded={handleBillingNoteAdded}
          noteSource={isClientView ? 'via Client View' : 'Statement'}
          requireClientViewConfirm={isClientView}
          staffName={currentUserInfo?.name || currentUserInfo?.email}
          clientName={viewingAsClient?.organizationName}
          viewAsOrganizationId={viewAsOrganizationId}
        />
      )}

      {cancellation.state.isOpen && (
        <CancelRequestModal
          service={cancellation.state.service}
          orderType={cancellation.state.order?.orderType}
          show
          onHide={cancellation.close}
          values={cancellation.state.order}
          onValuesChange={cancellation.updateOrderField}
          onSubmit={handleCancellationSubmit}
          cancelRequestSubmitting={cancellation.state.submitting}
        />
      )}

      {closingChange.state.isOpen && (
        <ClosingChangeRequestModal
          show
          onHide={() => !closingChange.state.submitting && closingChange.close()}
          values={closingChange.state.values}
          onValuesChange={closingChange.updateField}
          onSubmit={handleClosingChangeSubmit}
          submittingChangeRequest={closingChange.state.submitting}
        />
      )}

      <ClientViewConfirmModal
        show={Boolean(clientViewConfirm)}
        onHide={() => setClientViewConfirm(null)}
        onConfirm={handleClientViewConfirm}
        staffName={currentUserInfo?.name || currentUserInfo?.email || 'Staff'}
        clientName={viewingAsClient?.organizationName || 'Client'}
        title={clientViewConfirm?.title}
        submitting={
          Boolean(actionLoading[`check-${clientViewConfirm?.row?._id}`]) ||
          billingNoteSubmitting ||
          cancellation.state.submitting ||
          closingChange.state.submitting
        }
      />

      {markPaidConfirmRow && (
        <MarkInvoicePaidModal
          show={showMarkPaidModal}
          row={markPaidConfirmRow}
          onExited={handleMarkPaidModalExited}
          onHide={() => {
            if (markPaidSubmitting) return;
            closeMarkPaidConfirm();
          }}
          onSubmit={markPaidMultiInvoice ? closeMarkPaidConfirm : handleMarkPaidConfirm}
          submitting={markPaidSubmitting}
        />
      )}

      {undoCancellationRow && (
        <UndoCancellationModal
          show={showUndoCancellationModal}
          onExited={() => setUndoCancellationRow(null)}
          onHide={() => {
            if (undoCancellationSubmitting) return;
            setShowUndoCancellationModal(false);
          }}
          onSubmit={handleUndoCancellationConfirm}
          submitting={undoCancellationSubmitting}
        />
      )}

      <ServiceSlidingBar
        service={serviceMetadata?.service}
        orderType={serviceMetadata?.orderType}
        onClose={() => setServiceMetadata(null)}
        refreshOrders={refetchAfterSidebarUpdate}
        rowOrderId={serviceMetadata?.rowOrderId}
      />
    </>
  );
};

export default UnpaidStatementsInvoicesTab;
